import { supabase } from './supabase';
import { fetchSignedUrlsByFileId } from './caseFileSignedUrls';

const SMILE_FRONT_PHOTO_TYPE = 'smileFront';

/**
 * URL affichable de la photo « Sourire de face » de la première conception du patient
 * (ordre chronologique de création). null si aucune photo.
 */
export async function fetchPatientSmileFrontPhotoUrl(patientId) {
  if (!patientId) return null;

  const { data: cases, error: casesError } = await supabase
    .from('cases')
    .select('id, created_date')
    .eq('patient_id', patientId)
    .order('created_date', { ascending: true });

  if (casesError) throw casesError;
  if (!cases?.length) return null;

  const caseOrder = cases.map((c) => c.id);

  const { data: photos, error: photosError } = await supabase
    .from('case_photos')
    .select('id, case_id, file_path, file_name, photo_type, upload_date')
    .in('case_id', caseOrder)
    .eq('photo_type', SMILE_FRONT_PHOTO_TYPE)
    .eq('is_active', true);

  if (photosError) throw photosError;
  if (!photos?.length) return null;

  const byCase = new Map();
  for (const p of photos) {
    if (!p.file_path) continue;
    const prev = byCase.get(p.case_id);
    if (!prev || new Date(p.upload_date) > new Date(prev.upload_date)) {
      byCase.set(p.case_id, p);
    }
  }

  const chosen = caseOrder.map((id) => byCase.get(id)).find(Boolean);
  if (!chosen) return null;

  const signed = await fetchSignedUrlsByFileId([
    {
      id: chosen.id,
      filePath: chosen.file_path,
      name: chosen.file_name,
    },
  ]);
  return signed[chosen.id] || null;
}

/** Photo « Sourire de face » du cas courant (fiche cas). */
export async function fetchCaseSmileFrontPhotoUrl(caseId) {
  if (!caseId) return null;

  const { data: photo, error } = await supabase
    .from('case_photos')
    .select('id, file_path, file_name')
    .eq('case_id', caseId)
    .eq('photo_type', SMILE_FRONT_PHOTO_TYPE)
    .eq('is_active', true)
    .order('upload_date', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!photo?.file_path) return null;

  const signed = await fetchSignedUrlsByFileId([
    { id: photo.id, filePath: photo.file_path, name: photo.file_name },
  ]);
  return signed[photo.id] || null;
}

export function patientInitialsFromName(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 3);
}
